import React from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";

import {
  Alert,
  List,
  ListItem,
  CircularProgress,
  ListItemIcon,
} from "@mui/material";
import { Menu as MenuIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";

import SignatureMouthpiecesListItem from "./SignatureMouthpiecesListItem";

const QUERY = gql`
  query (
    $first: Int
    $after: String
    $filters: SignatureMouthpieces_FiltersInput!
  ) {
    signatureMouthpieces(
      first: $first
      after: $after
      filters: $filters
      sort: "weight"
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }

      edges {
        cursor
        node {
          id

          ...SignatureMouthpiecesListItem_signatureMouthpiece
        }
      }
    }
  }

  ${SignatureMouthpiecesListItem.fragments.signatureMouthpiece}
`;

const MUTATION = gql`
  mutation ($ids: [ID]!) {
    ReorderSignatureMouthpieces(signatureMouthpieceIds: $ids) {
      signatureMouthpieces {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

export default function ArticlesPage({ search }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { data, loading, fetchMore } = useQuery(QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: {
      first: 20,
      filters: { search },
    },
  });

  const [reorderSignatureMouthpieces] = useMutation(MUTATION);

  const [isDragging, setIsDragging] = React.useState(false);
  const onDragStart = React.useCallback(() => setIsDragging(true), []);

  const loadNextOnDragEnd = React.useRef();
  const onDragEnd = React.useCallback(
    async (result) => {
      // dropped outside the list
      if (!result.destination) return;

      const snackbarKey = enqueueSnackbar("Enregistrement en cours …", {
        persist: true,
        action: <CircularProgress size={30} color="inherit" />,
      });

      const reorderedEdges = Array.from(data.signatureMouthpieces.edges);
      const [removed] = reorderedEdges.splice(result.source.index, 1);
      reorderedEdges.splice(result.destination.index, 0, removed);

      await reorderSignatureMouthpieces({
        variables: {
          ids: reorderedEdges.map(({ node }) => node.id),
        },
        optimisticResponse: {
          ReorderSignatureMouthpieces: {
            signatureMouthpieces: {
              edges: reorderedEdges,
              __typename: "SignatureMouthpieceConnection",
            },
            __typename: "ReorderSignatureMouthpieces",
          },
        },
        update: (store, { data: { ReorderSignatureMouthpieces } }) => {
          const getEdge = (articleId) =>
            data.signatureMouthpieces.edges.find(
              ({ node }) => node.id === articleId
            );

          const edges = ReorderSignatureMouthpieces.signatureMouthpieces.edges
            .map(({ node }) => getEdge(node.id))
            .filter(Boolean);

          store.writeQuery({
            query: QUERY,
            variables: {
              filters: { search },
            },
            data: {
              ...data,
              signatureMouthpieces: {
                ...data.signatureMouthpieces,
                edges,
              },
            },
          });
        },
      });

      closeSnackbar(snackbarKey);
      setIsDragging(false);

      if (loadNextOnDragEnd.current) {
        loadNextOnDragEnd.current = false;

        fetchMore({
          variables: {
            after: reorderedEdges[reorderedEdges.length - 1]?.cursor,
          },
        });
      }
    },
    [
      data,
      fetchMore,
      search,
      reorderSignatureMouthpieces,
      enqueueSnackbar,
      closeSnackbar,
    ]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <List dense>
        <InfiniteScroll
          scrollableTarget="Layout-Main"
          dataLength={data?.signatureMouthpieces.edges.length ?? 0}
          hasMore={data?.signatureMouthpieces.pageInfo.hasNextPage ?? loading}
          next={() => {
            if (isDragging) {
              loadNextOnDragEnd.current = true;
              return;
            }

            fetchMore({
              variables: {
                after: data?.signatureMouthpieces.pageInfo.endCursor,
              },
            });
          }}
          loader={
            isDragging ? (
              <ListItem severity="info" component={Alert}>
                Impossible de charger la suite pendant un drag’n’drop.
              </ListItem>
            ) : (
              <Layout.Loading />
            )
          }
        >
          <Droppable
            droppableId="droppable"
            isDropDisabled={!!search}
            // Too much bugs right now w/ dnd; at least wait for https://github.com/atlassian/react-beautiful-dnd/issues/2014
            //
            // mode="virtual"
            // renderClone={(provided, snapshot, rubric) => {
            //   const signatureMouthpiece =
            //     data?.signatureMouthpieces.edges[rubric.source.index].node;
            //
            //   return (
            //     <SignatureMouthpiecesListItem
            //       signatureMouthpiece={signatureMouthpiece}
            //       disableDrag={!!search}
            //       handleProps={provided.dragHandleProps}
            //       ref={provided.innerRef}
            //       ContainerComponent="div"
            //       ContainerProps={provided.draggableProps}
            //     />
            //   );
            // }}
          >
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {data?.signatureMouthpieces.edges.map(({ node }, index) => (
                  <Draggable
                    key={node.id}
                    draggableId={node.id}
                    index={index}
                    isDragDisabled={!!search}
                  >
                    {(provided, snapshot) => (
                      <SignatureMouthpiecesListItem
                        signatureMouthpiece={node}
                        ref={provided.innerRef}
                        ContainerProps={provided.draggableProps}
                        component={Link}
                        to={`/signature-mouthpieces/${node.id}`}
                        button
                      >
                        {!search && (
                          <ListItemIcon {...provided.dragHandleProps}>
                            <MenuIcon />
                          </ListItemIcon>
                        )}
                      </SignatureMouthpiecesListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </InfiniteScroll>
      </List>
    </DragDropContext>
  );
}
